import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

export type AiGuidanceStatus = 'PROPOSED' | 'APPROVED' | 'REJECTED';
export type AiGuidanceSection = 'PRE_APPLICATION' | 'KICKSTART' | 'DEBTORS_FINANCE';

/** A Notes for AI note as it stands. */
export interface AiGuidanceNote {
  id: string;
  text: string;
  status: AiGuidanceStatus;
  status_display: string;
  by: string;
  at: string;
  source_ref: string;
  section: AiGuidanceSection;
  section_display: string;
  purpose: string;
  purpose_display: string;
  response_ref: string;
  response_summary: string;
}

/** A summary of one group's approved notes. */
export interface AiGuidanceSummary {
  id: string;
  text: string;
  note_count: number;
  at: string;
  by: string;
}

/** One section and kind of AI response: its notes and its summary. */
export interface AiGuidanceGroup {
  section: AiGuidanceSection;
  section_display: string;
  purpose: string;
  purpose_display: string;
  approved_notes: number;
  /** Written and waiting for a decision. */
  proposal: AiGuidanceSummary | null;
  /** The accepted summary: what the AI receives. */
  in_use: AiGuidanceSummary | null;
  /** The approved notes have changed since the summary in use was written. */
  out_of_date: boolean;
}

export interface AiGuidanceReview {
  notes: AiGuidanceNote[];
  groups: AiGuidanceGroup[];
}

export interface AiGuidanceDraft {
  text: string;
  response_ref: string;
  section: AiGuidanceSection;
  source_ref: string;
}

@Injectable({ providedIn: 'root' })
export class AiGuidanceService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/ai/guidance/`;

  /** The notes the signed-in person wrote on this file. */
  mine(section: AiGuidanceSection, sourceRef: string): Observable<AiGuidanceNote[]> {
    return this.http.get<AiGuidanceNote[]>(this.base, {
      params: { section, source_ref: sourceRef },
    });
  }

  propose(draft: AiGuidanceDraft): Observable<AiGuidanceNote> {
    return this.http.post<AiGuidanceNote>(this.base, draft);
  }

  /** Every note and summary. Platform admin only. */
  review(): Observable<AiGuidanceReview> {
    return this.http.get<AiGuidanceReview>(`${this.base}review/`);
  }

  /** An edit, a decision, or both. Platform admin only. */
  act(
    id: string,
    change: { status?: AiGuidanceStatus; text?: string },
  ): Observable<AiGuidanceNote> {
    return this.http.post<AiGuidanceNote>(`${this.base}${id}/`, change);
  }

  /** Write a summary of one group's approved notes. Only ever on an admin's press. */
  generateSummary(section: AiGuidanceSection, purpose: string): Observable<AiGuidanceSummary> {
    return this.http.post<AiGuidanceSummary>(`${this.base}summaries/`, { section, purpose });
  }

  decideSummary(
    id: string,
    status: 'APPROVED' | 'REJECTED',
    text: string,
  ): Observable<AiGuidanceSummary> {
    return this.http.post<AiGuidanceSummary>(`${this.base}summaries/${id}/`, { status, text });
  }
}
